import React from "react";
import { Link } from "react-router-dom";
import { createPageStyles } from "./MarketingPageStyles";
import { detectPageLocale } from "./pageLocale";

const styles = createPageStyles("dark", "luna");

type Locale = "ko" | "en" | "ja";

const LOCALE_STORAGE_KEY = "lunatarot-about-locale";
const locales: Locale[] = ["ko", "en", "ja"];
const localeLabels: Record<Locale, string> = { ko: "KO", en: "EN", ja: "JA" };

const content: Record<
  Locale,
  {
    crumb: string;
    title: string;
    accentTitle: string;
    subtitle: string;
    sections: Array<{ title: string; body?: string; list?: string[] }>;
    privacyTitle: string;
    privacyBody: string;
    audienceTitle: string;
    audience: string[];
  }
> = {
  ko: {
    crumb: "LunaTarot About",
    title: "LunaTarot,",
    accentTitle: "밤하늘의 작은 리딩 앱.",
    subtitle:
      "“Mystic Vintage Gold Tarot” 스타일의 오리지널 78장 덱과 함께, 질문과 감정을 조용히 기록하는 타로 저널 앱입니다. 회원가입 없이 바로 열 수 있고, 더 긴 해석이 필요할 때만 AI 분석을 불러옵니다.",
    sections: [
      {
        title: "주요 특징",
        list: [
          "오리지널 78장 Mystic Vintage Gold Tarot 덱",
          "오늘의 카드, 3장 리딩 등 다양한 스프레드",
          "리딩 히스토리와 질문 기록",
          "몰입을 돕는 어두운 밤하늘 톤의 인터페이스",
        ],
      },
    ],
    privacyTitle: "개인정보 및 보안",
    privacyBody:
      "LunaTarot는 별도의 회원가입이나 로그인을 요구하지 않습니다. 리딩 기록은 기기 내에 저장되며, 앱 삭제 시 함께 제거됩니다. AI 분석 기능을 사용하는 경우 질문/메모와 카드 정보가 응답 생성을 위해 Google Gemini API로 전송될 수 있습니다. 자세한 내용은 개인정보 처리방침에서 확인할 수 있습니다.",
    audienceTitle: "대상 사용자",
    audience: [
      "하루를 차분하게 정리하는 루틴으로 타로를 즐기고 싶은 분",
      "카드 그림과 상징을 감상하는 것을 좋아하는 분",
      "복잡한 커뮤니티 기능 없이 조용한 개인 리딩을 원하는 분",
    ],
  },
  en: {
    crumb: "LunaTarot About",
    title: "LunaTarot,",
    accentTitle: "A small reading app for the night.",
    subtitle:
      "LunaTarot is a quiet tarot journaling app built around an original 78-card deck in the Mystic Vintage Gold Tarot style. It opens without sign-up, and AI analysis appears only when you want a longer interpretation.",
    sections: [
      {
        title: "Key Features",
        list: [
          "Original 78-card Mystic Vintage Gold Tarot deck",
          "Multiple spreads including daily cards and 3-card readings",
          "Private reading history and question journaling",
          "A dark night-sky interface designed for focus",
        ],
      },
    ],
    privacyTitle: "Privacy and Security",
    privacyBody:
      "LunaTarot does not require account creation or login. Reading history stays on the device and is removed when the app is deleted. When AI analysis is used, your question or memo and selected card information may be sent to Google Gemini API to generate a response. Full details are available in the privacy policy.",
    audienceTitle: "Who It Is For",
    audience: [
      "People who want tarot as a calm daily reflection ritual",
      "People who enjoy card imagery and symbolism",
      "People who prefer a quiet personal reading tool without community clutter",
    ],
  },
  ja: {
    crumb: "LunaTarot About",
    title: "LunaTarot,",
    accentTitle: "夜のための小さなリーディングアプリ。",
    subtitle:
      "「Mystic Vintage Gold Tarot」スタイルのオリジナル78枚デッキとともに、質問や感情を静かに記録できるタロットジャーナルアプリです。会員登録なしですぐに使え、必要なときだけ AI 分析を呼び出せます。",
    sections: [
      {
        title: "主な特徴",
        list: [
          "オリジナル78枚の Mystic Vintage Gold Tarot デッキ",
          "今日のカードや3枚引きなど複数のスプレッド",
          "リーディング履歴と質問の記録",
          "集中しやすい夜空トーンのインターフェース",
        ],
      },
    ],
    privacyTitle: "プライバシーと安全性",
    privacyBody:
      "LunaTarot は会員登録やログインを必要としません。リーディング履歴は端末内に保存され、アプリ削除時に一緒に消去されます。AI 分析機能を使う場合、質問やメモ、選択したカード情報が Google Gemini API に送信されることがあります。詳しくはプライバシーポリシーをご確認ください。",
    audienceTitle: "こんな方に",
    audience: [
      "一日を静かに振り返る習慣としてタロットを楽しみたい方",
      "カードの絵柄や象徴を味わうのが好きな方",
      "コミュニティ機能よりも個人的で静かなリーディングを求める方",
    ],
  },
};

const LunaTarotAbout: React.FC = () => {
  const [locale, setLocale] = React.useState<Locale>("en");

  React.useEffect(() => {
    setLocale(detectPageLocale(LOCALE_STORAGE_KEY, locales, "en"));
  }, []);

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const copy = content[locale];

  return (
    <main style={styles.page}>
      <div style={styles.shell}>
        <div style={styles.topRow}>
          <Link to="/" style={styles.homeLink}>
            ← Daydream Factory
          </Link>
          <div style={styles.topRowMeta}>
            <div style={styles.localeSwitch} aria-label="Language switcher">
              {locales.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setLocale(item)}
                  style={{
                    ...styles.localeButton,
                    ...(locale === item ? styles.activeLocaleButton : {}),
                  }}
                >
                  {localeLabels[item]}
                </button>
              ))}
            </div>
            <span style={styles.crumb}>{copy.crumb}</span>
          </div>
        </div>

        <section style={styles.hero}>
          <h1 style={styles.title}>
            {copy.title}
            <br />
            <span style={styles.accentTitle}>{copy.accentTitle}</span>
          </h1>
          <p style={styles.subtitle}>{copy.subtitle}</p>
        </section>

        {copy.sections.map((section) => (
          <section key={section.title} style={styles.section}>
            <h2 style={styles.h2}>{section.title}</h2>
            <div style={styles.sectionCard}>
              {section.list ? (
                <ul style={styles.list}>
                  {section.list.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          </section>
        ))}

        <section style={styles.section}>
          <h2 style={styles.h2}>{copy.privacyTitle}</h2>
          <div style={styles.sectionCard}>
            <div style={styles.badgeRow}>
              <span style={styles.badge}>No Account</span>
              <span style={styles.badge}>No Login</span>
              <span style={styles.badge}>Local History</span>
            </div>
            <p style={styles.body}>
              {copy.privacyBody.split("개인정보 처리방침").length > 1 ||
              copy.privacyBody.split("privacy policy").length > 1 ||
              copy.privacyBody.split("プライバシーポリシー").length > 1 ? (
                <>
                  {copy.privacyBody
                    .replace("개인정보 처리방침", "")
                    .replace("privacy policy", "")
                    .replace("プライバシーポリシー", "")}
                  <Link to="/lunatarot/privacy" style={styles.link}>
                    {locale === "ko" ? "개인정보 처리방침" : locale === "ja" ? "プライバシーポリシー" : "privacy policy"}
                  </Link>
                  .
                </>
              ) : (
                copy.privacyBody
              )}
            </p>
          </div>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>{copy.audienceTitle}</h2>
          <div style={styles.sectionCard}>
            <ul style={styles.list}>
              {copy.audience.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </section>
      </div>
    </main>
  );
};

export default LunaTarotAbout;
